import { isNativePlatform } from '@/lib/platform';
import { Browser } from '@capacitor/browser';

export type Stay22OpenResult =
  | { status: 'opened'; url: string }
  | { status: 'blocked'; url: string };

export interface OpenStay22InNewTabOptions {
  url: string;
  onSuccess?: (url: string) => void;
  onBlocked?: (url: string) => void;
}

/**
 * Opens a Stay22 Allez or Map URL in a new browser tab (web) or in-app browser (native).
 * Must be called synchronously from a user-gesture handler.
 */
export function openStay22InNewTab(options: OpenStay22InNewTabOptions): Stay22OpenResult {
  const { url, onSuccess, onBlocked } = options;

  if (typeof window === 'undefined') {
    onBlocked?.(url);
    return { status: 'blocked', url };
  }

  if (isNativePlatform()) {
    void Browser.open({ url }).then(() => {
      onSuccess?.(url);
    });
    return { status: 'opened', url };
  }

  const popup = window.open(url, '_blank');
  if (!popup) {
    onBlocked?.(url);
    return { status: 'blocked', url };
  }

  try {
    popup.opener = null;
  } catch {
    // Tab may already be cross-origin; opener isolation is best-effort.
  }

  onSuccess?.(url);
  return { status: 'opened', url };
}
